/**
 * Sales tracking hook for HelpPetAI
 * Detects ?hp=X query parameter and sends tracking notification to backend
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface TrackingData {
  hp: number;
  page: string;
  referrer?: string;
  user_agent?: string;
  timestamp?: string;
}

/**
 * Hook to track sales link clicks from email campaigns
 * @param enabled - Whether tracking should be enabled for this page (default: true)
 */
export const useSalesTracking = (enabled: boolean = true) => {
  const location = useLocation();
  const hasTracked = useRef(false);

  useEffect(() => {
    // Only track once per page load
    if (!enabled || hasTracked.current) {
      return;
    }

    // Parse query parameters
    const searchParams = new URLSearchParams(location.search);
    const hpParam = searchParams.get('hp');

    // If hp parameter exists and is valid
    if (hpParam) {
      const hp = parseInt(hpParam, 10);
      
      // Validate hp is between 1 and 1000000
      if (hp >= 1 && hp <= 1000000) {
        // Mark as tracked to prevent duplicate requests
        hasTracked.current = true;

        // Prepare tracking data
        const trackingData: TrackingData = {
          hp,
          page: location.pathname,
          referrer: document.referrer || undefined,
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        };

        // Send tracking request to backend
        fetch(`${API_BASE_URL}/api/v1/sales-tracking/link-click`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(trackingData),
        })
          .then((response) => {
            if (response.ok) {
              console.log(`Sales tracking: Campaign #${hp} tracked on ${location.pathname}`);
            } else {
              console.warn('Sales tracking: Failed to track link click', response.status);
            }
          })
          .catch((error) => {
            console.error('Sales tracking: Error tracking link click', error);
          });
      }
    }
  }, [location, enabled]);
};

